import * as React from 'react';

import * as LabelPrimitive from '@radix-ui/react-label';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import {
    Controller,
    type ControllerProps,
    type FieldPath,
    type FieldValues,
    FormProvider,
    type UseFormReturn,
    useFormContext,
} from 'react-hook-form';
import { defineMessages, useIntl } from 'react-intl';

import { Label } from 'src/registry/ui/Label';
import { cn } from 'src/utils/shadcn';

// Basically zod doesn't allow objects as messages, so we have to somehow
// be able to define these messages outside of the react component lifecycle.
// This is a workaround for that. You can find in src/components/Form.tsx -
// FormMessage how we search for these messages inside this object only.
// It is a bit hacky but it is the only way to achieve it with zod and being able
// to contain the zod schema in the same file as the react query hooks.
export const validationMessages = defineMessages({
    validationErrorConfirmPassword: {
        id: 'validation-error-confirm-password',
        defaultMessage: 'Passwords do not match',
    },
    validationErrorFirstName: {
        id: 'validation-error-first-name',
        defaultMessage: 'First name must be between 3 and 24 characters',
    },
    validationErrorLastName: {
        id: 'validation-error-last-name',
        defaultMessage: 'Last name must be between 3 and 24 characters',
    },
    validationErrorEmail: {
        id: 'validation-error-email',
        defaultMessage: 'Please enter a valid email address',
    },
    validationErrorPassword: {
        id: 'validation-error-password',
        defaultMessage:
            'The password must be at least 8 characters long, contain letters and numbers, not be similar to the username or email, and not be commonly used.',
    },
    validationErrorRequired: {
        id: 'required-field',
        defaultMessage: 'Required',
    },
    validationErrorOldPassword: {
        id: 'validation-error-old-password',
        defaultMessage: 'Old password is incorrect',
    },
    validationErrorPricingPlanName: {
        id: 'validation-error-pricing-plan-name',
        defaultMessage: 'The name must be at least 5 characters long',
    },
    validationErrorName: {
        id: 'validation-error-name',
        defaultMessage: 'Name must be between 3 and 50 characters',
    },
    validationErrorGatewayExitEntryPoint: {
        id: 'validation-error-gateway-exit-entry-point',
        defaultMessage: 'Either exit or entry zone must be provided',
    },
    validationErrorActionIdleActionDisplay: {
        id: 'validation-error-action-idle-action-display',
        defaultMessage: 'Either action or idle action must be selected',
    },
    validationErrorURL: {
        id: 'validation-error-url',
        defaultMessage: 'The entered value is not a valid URL',
    },
    validationErrorLicensePlateFormat: {
        id: 'validation-error-license-plate-format',
        defaultMessage: 'Invalid license plate format',
    },
    validationErrorFullName: {
        id: 'validation-error-full-name',
        defaultMessage: 'Name must be between 3 and 255 characters',
    },
    validationErrorUsername: {
        id: 'validation-error-username',
        defaultMessage: 'Username must be between 3 and 150 characters',
    },
});

const formVariants = cva(``, {
    variants: {
        variant: {
            default: 'flex w-full flex-col gap-4 sm:gap-6',
            dialog: 'flex flex-col gap-4',
        },
    },
    defaultVariants: {
        variant: 'default',
    },
});

// const Form = FormProvider;
interface FormProps extends React.HTMLAttributes<HTMLFormElement>, VariantProps<typeof formVariants> {
    form: UseFormReturn<any>;
}

const Form = ({ form, children, variant, className, ...props }: FormProps) => {
    return (
        <FormProvider {...form}>
            <form className={cn(formVariants({ variant }), className)} {...props}>
                {children}
            </form>
        </FormProvider>
    );
};

type FormFieldContextValue<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
    name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue>({} as FormFieldContextValue);

const FormField = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
    ...props
}: ControllerProps<TFieldValues, TName>) => {
    return (
        <FormFieldContext.Provider value={{ name: props.name }}>
            <Controller {...props} />
        </FormFieldContext.Provider>
    );
};

const useFormField = () => {
    const fieldContext = React.useContext(FormFieldContext);
    const itemContext = React.useContext(FormItemContext);
    const { getFieldState, formState } = useFormContext();

    const fieldState = getFieldState(fieldContext.name, formState);

    if (!fieldContext) {
        throw new Error('useFormField should be used within <FormField>');
    }

    const { id } = itemContext;

    return {
        id,
        name: fieldContext.name,
        formItemId: `${id}-form-item`,
        formDescriptionId: `${id}-form-item-description`,
        formMessageId: `${id}-form-item-message`,
        ...fieldState,
    };
};

type FormItemContextValue = {
    id: string;
};

const FormItemContext = React.createContext<FormItemContextValue>({} as FormItemContextValue);

const formItemVariants = cva(``, {
    variants: {
        variant: {
            default: 'w-full space-y-1.5',
            dialog: 'flex flex-col gap-2 md:flex-row md:gap-4',
        },
    },
    defaultVariants: {
        variant: 'default',
    },
});

const FormItem = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof formItemVariants>
>(({ className, variant, ...props }, ref) => {
    const id = React.useId();

    return (
        <FormItemContext.Provider value={{ id }}>
            <div ref={ref} className={cn(formItemVariants({ variant }), className)} {...props} />
        </FormItemContext.Provider>
    );
});
FormItem.displayName = 'FormItem';

const FormControlStack = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    ({ className, ...props }, ref) => {
        return (
            <div
                ref={ref}
                className={cn('-m-1 flex w-full flex-col gap-1 overflow-hidden p-1', className)}
                {...props}
            />
        );
    },
);
FormControlStack.displayName = 'FormControlStack';

const formLabelVariants = cva('shrink-0 font-semibold text-gray-700', {
    variants: {
        variant: {
            default: '',
            dialog: 'w-full md:max-w-36 md:text-right',
        },
    },
    defaultVariants: {
        variant: 'default',
    },
});

const FormLabel = React.forwardRef<
    React.ComponentRef<typeof LabelPrimitive.Root>,
    React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root> &
        VariantProps<typeof formLabelVariants> & { required?: boolean }
>(({ className, variant, children, required, ...props }, ref) => {
    const { formItemId } = useFormField();

    return (
        <Label ref={ref} className={cn(formLabelVariants({ variant }), className)} htmlFor={formItemId} {...props}>
            {children}
            {required ? <span className="text-primary-800">*</span> : null}
        </Label>
    );
});
FormLabel.displayName = 'FormLabel';

const FormControl = React.forwardRef<React.ComponentRef<typeof Slot>, React.ComponentPropsWithoutRef<typeof Slot>>(
    ({ ...props }, ref) => {
        const { error, formItemId, formDescriptionId, formMessageId } = useFormField();

        return (
            <Slot
                ref={ref}
                id={formItemId}
                aria-describedby={!error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`}
                aria-invalid={!!error}
                {...{ error, ...props }}
            />
        );
    },
);
FormControl.displayName = 'FormControl';

const FormDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
    ({ className, ...props }, ref) => {
        const { formDescriptionId } = useFormField();

        return (
            <p
                ref={ref}
                id={formDescriptionId}
                className={cn('text-sm text-gray-500 dark:text-gray-400', className)}
                {...props}
            />
        );
    },
);
FormDescription.displayName = 'FormDescription';

interface FormMessageProps extends React.HTMLAttributes<HTMLParagraphElement> {
    index?: number;
}

const FormMessage = React.forwardRef<HTMLParagraphElement, FormMessageProps>(
    ({ className, children, index, ...props }, ref) => {
        const { error, formMessageId } = useFormField();
        const { formatMessage } = useIntl();
        let errorMessage = error?.message;

        if (index != null && error != null && Array.isArray(error) && error[index] != null) {
            errorMessage = error[index].message;
        }

        // Check out src/utils/messages.ts for a bit more context on this
        // but essentially we want to be able to translate messages without having to
        // implement them at runtime
        if (errorMessage && Object.prototype.hasOwnProperty.call(validationMessages, errorMessage)) {
            errorMessage = formatMessage(validationMessages[errorMessage as keyof typeof validationMessages]);
        }
        const body = errorMessage ? String(errorMessage) : children;

        return (
            <p
                ref={ref}
                id={formMessageId}
                className={cn('text-sm text-gray-500', errorMessage && 'text-red-500 dark:text-red-900', className)}
                {...props}
            >
                {body}
            </p>
        );
    },
);
FormMessage.displayName = 'FormMessage';

export {
    Form,
    FormControl,
    FormControlStack,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    useFormField,
};
